import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useUser } from '../contexts/UserContext';
import { apiService } from '../services/apiService';
import { Clock, Target, TrendingUp, BookOpen, CheckCircle, Award, ArrowLeft, Settings } from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell
} from 'recharts';

interface AnalyticsData {
  summary: {
    totalTests: number;
    latestScore: number;
    avgScore: number;
    improvement: number;
    projectedScore: number;
  };
  testScores: Array<{
    contestId: string;
    contestName: string;
    date: string;
    score: number;
    accuracy: number;
    timeSpent: number;
    questionsAttempted: number;
  }>;
  topicPerformance: Array<{
    topic: string;
    subtopic?: string;
    correct: number;
    total: number;
    accuracy: number;
    avgTimeSpent: number;
    category: 'strong' | 'moderate' | 'weak';
  }>;
  difficultyBreakdown: Array<{
    name: string;
    difficulty: string;
    value: number;
    target: number;
    correct: number;
    total: number;
  }>;
  timeManagement: Array<{
    difficulty: string;
    timeSpent: number;
    questionsCompleted: number;
    target: number;
    accuracy: number;
  }>;
}

interface AMCAnalyticsProps {
  onBack: () => void;
}

const Container = styled.div`
  min-height: 100vh;
  background: #f9fafb;
  padding: 24px;
`;

const ContentWrapper = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Card = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 24px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #6b7280;
  background: none;
  border: none;
  cursor: pointer;
  
  &:hover {
    color: #1f2937;
  }
`;

const Title = styled.h1`
  font-size: 1.875rem;
  font-weight: bold;
  color: #1f2937;
  margin: 0;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Select = styled.select`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
`;

const AdminButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #f3f4f6;
  color: #374151;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  
  &:hover {
    background: #e5e7eb;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const StatCard = styled.div<{ color: string }>`
  background: ${props => props.color};
  padding: 16px;
  border-radius: 8px;
`;

const StatCardInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StatInfo = styled.div``;

const StatLabel = styled.p`
  font-size: 0.875rem;
  font-weight: 500;
  margin: 0 0 4px 0;
`;

const StatValue = styled.p`
  font-size: 1.5rem;
  font-weight: bold;
  margin: 0;
`;

const ChartContainer = styled.div`
  height: 400px;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 16px 0;
`;

const LoadingContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LoadingContent = styled.div`
  text-align: center;
`;

const Spinner = styled.div`
  width: 48px;
  height: 48px;
  border: 2px solid #e5e7eb;
  border-top: 2px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorText = styled.p`
  color: #dc2626;
  margin-bottom: 16px;
`;

const Button = styled.button`
  padding: 8px 16px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  
  &:hover {
    background: #2563eb;
  }
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  
  @media (min-width: 1024px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const TimeManagementGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const TimeCard = styled.div`
  padding: 24px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`;

const TimeCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const TimeCardTitle = styled.h3`
  font-weight: 500;
  color: #1f2937;
  margin: 0;
`;

const TimeStats = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TimeStat = styled.div`
  display: flex;
  justify-content: space-between;
`;

const TimeStatLabel = styled.span`
  font-size: 0.875rem;
  color: #6b7280;
`;

const TimeStatValue = styled.span<{ isOver?: boolean }>`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${props => props.isOver ? '#dc2626' : '#059669'};
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 12px;
  background: #e5e7eb;
  border-radius: 6px;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ width: number; isOver?: boolean }>`
  height: 100%;
  width: ${props => Math.min(100, props.width)}%;
  background: ${props => props.isOver ? '#f87171' : '#34d399'};
  border-radius: 6px;
`;

const RecommendationsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const RecommendationCard = styled.div``;

const RecommendationTitle = styled.h3`
  font-weight: 500;
  color: #1f2937;
  margin: 0 0 12px 0;
`;

const RecommendationsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const RecommendationItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  background: #dbeafe;
  border-radius: 8px;
`;

const RecommendationText = styled.p`
  font-size: 0.875rem;
  color: #1e3a8a;
  margin: 0;
`;

const ProjectionCard = styled.div`
  padding: 16px;
  background: #dcfce7;
  border-radius: 8px;
`;

const ProjectionGrid = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const ProjectionLabel = styled.span`
  font-size: 0.875rem;
  color: #166534;
`;

const ProjectionValue = styled.span`
  font-size: 1.125rem;
  font-weight: bold;
  color: #15803d;
`;

const ProjectionNote = styled.div`
  font-size: 0.75rem;
  color: #166534;
  margin-top: 8px;
`;

const AdminPanel = styled.div`
  background: #f3f4f6;
  border-radius: 8px;
  padding: 24px;
  border-left: 4px solid #3b82f6;
`;

const AdminTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 12px 0;
`;

const AdminGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const AdminField = styled.div``;

const AdminLabel = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
`;

const AdminInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
`;

const AMCAnalytics: React.FC<AMCAnalyticsProps> = ({ onBack }) => {
  const { token } = useUser();
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeframe, setTimeframe] = useState('all');
  const [showAdmin, setShowAdmin] = useState(false);
  const [testDate, setTestDate] = useState('2025-02-15');
  const [targetScore, setTargetScore] = useState(140);

  useEffect(() => {
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeframe, token]);

  const fetchAnalytics = async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      const response = await apiService.get(`/amc/analytics?timeframe=${timeframe}`);
      setAnalyticsData(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  };

  const calculateProjectedScore = (testScores: any[]) => {
    if (testScores.length < 2) return testScores[testScores.length - 1]?.score || 0;
    
    const n = testScores.length;
    const xValues = testScores.map((_, index) => index);
    const yValues = testScores.map(test => test.score);
    
    const sumX = xValues.reduce((a, b) => a + b, 0);
    const sumY = yValues.reduce((a, b) => a + b, 0);
    const sumXY = xValues.reduce((sum, x, i) => sum + x * yValues[i], 0);
    const sumXX = xValues.reduce((sum, x) => sum + x * x, 0);
    
    const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;
    
    // Calculate weeks until test date
    const daysUntilTest = Math.ceil((new Date(testDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
    const weeksUntilTest = Math.max(1, daysUntilTest / 7);
    const futureTestIndex = n + Math.round(weeksUntilTest);
    const projectedScore = slope * futureTestIndex + intercept;
    
    return Math.min(150, Math.max(30, projectedScore));
  };

  const generateRecommendations = () => {
    if (!analyticsData) return [];
    
    const recommendations = [];
    const { summary, topicPerformance, timeManagement } = analyticsData;
    
    if (summary.latestScore > 0 && summary.latestScore < 100) {
      recommendations.push("Focus on accuracy - aim for 100+ score to qualify for AIME");
    }
    
    const weakTopics = topicPerformance.filter(topic => topic.category === 'weak').slice(0, 2);
    if (weakTopics.length > 0) {
      recommendations.push(`Prioritize ${weakTopics[0].topic} - your weakest area at ${weakTopics[0].accuracy}%`);
    }
    
    const easyTime = timeManagement.find(tm => tm.difficulty.includes('Easy'));
    if (easyTime && easyTime.timeSpent > easyTime.target) {
      recommendations.push("Spend less time on easy questions - aim for 1.5 minutes per problem");
    }
    
    if (summary.totalTests < 5) {
      recommendations.push("Take more practice tests to improve prediction accuracy");
    }
    
    return recommendations;
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0]?.payload;
      return (
        <div style={{ 
          background: 'white', 
          padding: '12px', 
          border: '1px solid #e5e7eb', 
          borderRadius: '8px', 
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' 
        }}>
          <p style={{ fontWeight: '600', marginBottom: '4px' }}>{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.color, margin: '2px 0' }}>
              {entry.name}: {entry.value}%
              {data?.displayText && (
                <span style={{ color: '#6b7280', marginLeft: '8px' }}>
                  ({data.displayText})
                </span>
              )}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <LoadingContainer>
        <LoadingContent>
          <Spinner />
          <p style={{ color: '#6b7280' }}>Loading analytics...</p>
        </LoadingContent>
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <LoadingContainer>
        <LoadingContent>
          <ErrorText>{error}</ErrorText>
          <Button onClick={fetchAnalytics}>
            Try Again
          </Button>
        </LoadingContent>
      </LoadingContainer>
    );
  }

  if (!analyticsData) {
    return (
      <LoadingContainer>
        <LoadingContent>
          <p style={{ color: '#6b7280' }}>No analytics data available</p>
          <Button onClick={onBack} style={{ marginTop: '16px' }}>
            Back to Dashboard
          </Button>
        </LoadingContent>
      </LoadingContainer>
    );
  }

  const { summary, testScores, topicPerformance, difficultyBreakdown, timeManagement } = analyticsData;
  const projectedScore = calculateProjectedScore(testScores);

  // Prepare data for charts
  const chartData = testScores.map((test, index) => ({
    ...test,
    testNumber: index + 1,
    date: new Date(test.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    projectedScore: null as number | null // No projection data for actual tests
  }));

  // Add projected score to chart data
  if (testScores.length > 0) {
    // Add projection connection: last actual test with projection data
    chartData[chartData.length - 1] = {
      ...chartData[chartData.length - 1],
      projectedScore: chartData[chartData.length - 1].score // Start projection from last actual
    };
    
    // Add projected point
    chartData.push({
      ...testScores[testScores.length - 1],
      score: null as any, // Don't connect solid line to projected point
      projectedScore: projectedScore,
      testNumber: testScores.length + 1,
      date: 'Projected',
      contestName: 'Test Day Projection'
    });
  }

  const topicColors = {
    'strong': '#10B981',
    'moderate': '#10B981', 
    'weak': '#10B981'
  };

  return (
    <Container>
      <ContentWrapper>
        
        {/* Header */}
        <Card>
          <Header>
            <HeaderLeft>
              <BackButton onClick={onBack}>
                <ArrowLeft size={20} />
                <span>Back to Dashboard</span>
              </BackButton>
              <Title>AMC Performance Analytics</Title>
            </HeaderLeft>
            <HeaderRight>
              <Select
                value={timeframe}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setTimeframe(e.target.value)}
              >
                <option value="all">All Time</option>
                <option value="6months">Last 6 Months</option>
                <option value="3months">Last 3 Months</option>
              </Select>
              <AdminButton onClick={() => setShowAdmin(!showAdmin)}>
                <Settings size={16} />
                <span>Admin</span>
              </AdminButton>
            </HeaderRight>
          </Header>
          
          {/* Key Stats */}
          <StatsGrid>
            <StatCard color="#dbeafe">
              <StatCardInner>
                <StatInfo>
                  <StatLabel style={{ color: '#2563eb' }}>Tests Taken</StatLabel>
                  <StatValue style={{ color: '#1e40af' }}>{summary.totalTests}</StatValue>
                </StatInfo>
                <BookOpen size={32} color="#2563eb" />
              </StatCardInner>
            </StatCard>
            
            <StatCard color="#dcfce7">
              <StatCardInner>
                <StatInfo>
                  <StatLabel style={{ color: '#059669' }}>Latest Score</StatLabel>
                  <StatValue style={{ color: '#047857' }}>{summary.latestScore}</StatValue>
                </StatInfo>
                <Target size={32} color="#059669" />
              </StatCardInner>
            </StatCard>
            
            <StatCard color="#f3e8ff">
              <StatCardInner>
                <StatInfo>
                  <StatLabel style={{ color: '#7c3aed' }}>Projected Score</StatLabel>
                  <StatValue style={{ color: '#6d28d9' }}>{Math.round(projectedScore)}</StatValue>
                </StatInfo>
                <TrendingUp size={32} color="#7c3aed" />
              </StatCardInner>
            </StatCard>
            
            <StatCard color="#fed7aa">
              <StatCardInner>
                <StatInfo>
                  <StatLabel style={{ color: '#ea580c' }}>Improvement</StatLabel>
                  <StatValue style={{ color: '#c2410c' }}>
                    {summary.improvement > 0 ? '+' : ''}{summary.improvement}
                  </StatValue>
                </StatInfo>
                <Award size={32} color="#ea580c" />
              </StatCardInner>
            </StatCard>
          </StatsGrid>
        </Card>

        {/* Score Progress Chart */}
        <Card>
          <SectionTitle>Score Progress & Projection</SectionTitle>
          <ChartContainer>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis domain={[60, 150]} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                {/* Actual scores - solid line (stops at last actual test) */}
                <Line 
                  type="monotone" 
                  dataKey="score" 
                  stroke="#3F8EF6" 
                  strokeWidth={3}
                  dot={(props: any) => {
                    // Only show dots for actual tests, not projected
                    if (props.payload?.date !== 'Projected') {
                      return <circle cx={props.cx} cy={props.cy} r={4} fill="#3F8EF6" strokeWidth={2} />;
                    }
                    return <g />;
                  }}
                  connectNulls={false}
                  name="Actual Scores"
                />
                {/* Projection line - only the last segment, dotted */}
                {testScores.length > 0 && chartData.length > 1 && (
                  <Line 
                    type="monotone" 
                    dataKey="projectedScore" 
                    stroke="#3F8EF6" 
                    strokeWidth={3}
                    strokeDasharray="8 8"
                    dot={(props: any) => {
                      // Green dot only for the projected point
                      if (props.payload?.date === 'Projected') {
                        return <circle cx={props.cx} cy={props.cy} r={4} fill="#A0F0D0" strokeWidth={2} />;
                      }
                      return <g />;
                    }}
                    connectNulls={false}
                    name=""
                    legendType="none"
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </ChartContainer>
        </Card>

        <GridContainer>
          {/* Topic Performance */}
          <Card>
            <SectionTitle>Topic Performance</SectionTitle>
            <div style={{ marginBottom: '16px', fontSize: '0.875rem', color: '#6b7280' }}>
              Accuracy percentage by topic (hover for detailed correct/total counts)
            </div>
            <ChartContainer style={{ height: '320px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topicPerformance}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="topic" />
                  <YAxis label={{ value: 'Accuracy (%)', angle: -90, position: 'insideLeft' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar 
                    dataKey="accuracy" 
                    radius={[4, 4, 0, 0]}
                  >
                    {topicPerformance.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={topicColors[entry.category] || '#6B7280'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
          </Card>

          {/* Difficulty Breakdown */}
          <Card>
            <SectionTitle>Performance by Difficulty</SectionTitle>
            <div style={{ marginBottom: '16px', fontSize: '0.875rem', color: '#6b7280' }}>
              <span style={{ color: '#A0F0D0' }}>●</span> Your Performance &nbsp;&nbsp;
              <span style={{ color: '#3F8EF6' }}>●</span> Target Benchmarks
            </div>
            <ChartContainer style={{ height: '320px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={difficultyBreakdown}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar 
                    dataKey="value" 
                    name="Your Performance %" 
                    fill="#A0F0D0" 
                    radius={[4, 4, 0, 0]} 
                  />
                  <Bar 
                    dataKey="target" 
                    name="Target %" 
                    fill="#3F8EF6" 
                    radius={[4, 4, 0, 0]} 
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
          </Card>
        </GridContainer>

        {/* Time Management */}
        <Card>
          <SectionTitle>Time Management Analysis</SectionTitle>
          <TimeManagementGrid>
            {timeManagement.map((item, index) => (
              <TimeCard key={index}>
                <TimeCardHeader>
                  <TimeCardTitle>{item.difficulty}</TimeCardTitle>
                  <Clock size={20} color="#6b7280" />
                </TimeCardHeader>
                <TimeStats>
                  <TimeStat>
                    <TimeStatLabel>Time Spent:</TimeStatLabel>
                    <TimeStatValue isOver={item.timeSpent > item.target}>
                      {item.timeSpent} min
                    </TimeStatValue>
                  </TimeStat>
                  <TimeStat>
                    <TimeStatLabel>Accuracy:</TimeStatLabel>
                    <TimeStatValue>{Math.round(item.accuracy)}%</TimeStatValue>
                  </TimeStat>
                  <TimeStat>
                    <TimeStatLabel>Target Time:</TimeStatLabel>
                    <TimeStatValue>{item.target} min</TimeStatValue>
                  </TimeStat>
                  <ProgressBar>
                    <ProgressFill 
                      width={(item.timeSpent / item.target) * 100}
                      isOver={item.timeSpent > item.target}
                    />
                  </ProgressBar>
                </TimeStats>
              </TimeCard>
            ))}
          </TimeManagementGrid>
        </Card>

        {/* Recommendations */}
        <Card>
          <SectionTitle>Personalized Recommendations</SectionTitle>
          <RecommendationsGrid>
            <RecommendationCard>
              <RecommendationTitle>Action Items</RecommendationTitle>
              <RecommendationsList>
                {generateRecommendations().map((rec, index) => (
                  <RecommendationItem key={index}>
                    <CheckCircle size={20} color="#3b82f6" style={{ marginTop: '2px', flexShrink: 0 }} />
                    <RecommendationText>{rec}</RecommendationText>
                  </RecommendationItem>
                ))}
              </RecommendationsList>
            </RecommendationCard>
            
            <RecommendationCard>
              <RecommendationTitle>Projected Performance</RecommendationTitle>
              <ProjectionCard>
                <ProjectionGrid>
                  <ProjectionLabel>Expected Test Day Score:</ProjectionLabel>
                  <ProjectionValue>{Math.round(projectedScore)}</ProjectionValue>
                </ProjectionGrid>
                <ProjectionGrid>
                  <ProjectionLabel>Days Until Test:</ProjectionLabel>
                  <ProjectionValue style={{ fontSize: '0.875rem' }}>
                    {Math.max(0, Math.ceil((new Date(testDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)))}
                  </ProjectionValue>
                </ProjectionGrid>
                <ProjectionNote>
                  Based on linear trend analysis of {summary.totalTests} tests
                </ProjectionNote>
              </ProjectionCard>
            </RecommendationCard>
          </RecommendationsGrid>
        </Card>

        {/* Admin Panel */}
        {showAdmin && (
          <AdminPanel>
            <AdminTitle>Admin Controls</AdminTitle>
            <AdminGrid>
              <AdminField>
                <AdminLabel>Official Test Date</AdminLabel>
                <AdminInput
                  type="date"
                  value={testDate}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTestDate(e.target.value)}
                />
              </AdminField>
              <AdminField>
                <AdminLabel>Target Score Goal</AdminLabel>
                <AdminInput
                  type="number"
                  value={targetScore}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTargetScore(Number(e.target.value))}
                />
              </AdminField>
              <AdminField>
                <AdminLabel>Weekly Practice Goal</AdminLabel>
                <Select style={{ width: '100%' }}>
                  <option>1 test per week</option>
                  <option>2 tests per week</option>
                  <option>3 tests per week</option>
                </Select>
              </AdminField>
            </AdminGrid>
          </AdminPanel>
        )}
      </ContentWrapper>
    </Container>
  );
};

export default AMCAnalytics;